import type React from "react";
import { Box, Group, Menu, SegmentedControl, Stack, Switch, Text } from "@mantine/core";
import { Button, StatusBadge } from "@xplatform/design-system";
import {
  IconAlertTriangleFilled,
  IconArrowLeft,
  IconChevronDown,
  IconDownload,
  IconFileSpreadsheet,
  IconJson,
  IconMailForward,
  IconRefresh,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { JobStatus, type JobViewModel } from "@models/job";
import BulkAcceptPopover from "./BulkAcceptPopover";

interface JobResultsTopbarProps {
  job: JobViewModel | null;
  jobId: string;
  jobLoading: boolean;
  hasResults: boolean;
  canCompare: boolean;
  batchView: "compare" | "review";
  onBatchViewChange: (value: "compare" | "review") => void;
  differentOnly: boolean;
  onDifferentOnlyChange: (value: boolean) => void;
  rowCount: number;
  editsCount: number;
  acceptedCount: number;
  onBack: () => void;
  onRefresh: () => void;
  onExport: () => void;
  onExportExcel: () => void;
  exportingExcel: boolean;
  /** Hidden for technical-manual, whose rows are reviewed one by one. */
  showBulkAccept: boolean;
  bulkOpen: boolean;
  onBulkOpenChange: (opened: boolean) => void;
  bulkRunning: boolean;
  bulkThreshold: number;
  onBulkThresholdChange: (value: number) => void;
  bulkEligibleCount: number;
  bulkSummary: string | null;
  onBulkCancel: () => void;
  onBulkApply: () => void;
  /** Rows the pipeline found no catalogue entry for. 0 hides the button —
   *  only wd-mapping produces that verdict. */
  missingCount: number;
  onRequestItems: () => void;
}

const JobResultsTopbar: React.FC<JobResultsTopbarProps> = ({
  job,
  jobId,
  jobLoading,
  hasResults,
  canCompare,
  batchView,
  onBatchViewChange,
  differentOnly,
  onDifferentOnlyChange,
  rowCount,
  editsCount,
  acceptedCount,
  onBack,
  onRefresh,
  onExport,
  onExportExcel,
  exportingExcel,
  showBulkAccept,
  bulkOpen,
  onBulkOpenChange,
  bulkRunning,
  bulkThreshold,
  onBulkThresholdChange,
  bulkEligibleCount,
  bulkSummary,
  onBulkCancel,
  onBulkApply,
  missingCount,
  onRequestItems,
}) => {
  const { t } = useTranslation();
  return (
    <Box className="job-results-page__topbar">
      <Group gap="sm" wrap="nowrap" align="center">
        <Button
          size="sm"
          variant="filled"
          leftSection={<IconArrowLeft size={16} />}
          onClick={onBack}
          aria-label={t("jobResultsPage.actions.backToService")}
        >
          {t("jobResultsPage.actions.back", { defaultValue: "Back" })}
        </Button>
        <Stack gap={2} align="flex-start" style={{ minWidth: 0 }}>
          <Text size="lg" fw={700} truncate>
            {job?.filename ?? jobId}
          </Text>
          {job && (
            <StatusBadge size="xs" status={job.status} variant="light">
              {t(`mappingServicePage.statuses.${job.status}`, {
                defaultValue: job.status,
              })}
            </StatusBadge>
          )}
          {(job?.status === JobStatus.Failed || job?.status === JobStatus.CompletedWithErrors) && job.failedFiles > 0 && (
            <Text size="sm" c="orange" role="status">
              <IconAlertTriangleFilled size={13} aria-hidden="true" />{" "}
              {t("jobResultsPage.sourcePdf.partial", { count: job.failedFiles })}
            </Text>
          )}
        </Stack>
      </Group>
      <Group gap="xs" className="job-results-page__topbar-actions">
        {missingCount > 0 && (
          <Button
            size="sm"
            variant="light"
            color="red"
            leftSection={<IconMailForward size={16} />}
            onClick={onRequestItems}
          >
            {t("jobResultsPage.itemRequest.action", {
              defaultValue: "Request catalogue entries",
            })}
            {` (${missingCount})`}
          </Button>
        )}
        {hasResults && canCompare && batchView === "compare" && (
          <Switch
            label={t("jobResultsPage.batchCompare.differentOnly")}
            checked={differentOnly}
            onChange={(event) => onDifferentOnlyChange(event.currentTarget.checked)}
            disabled={(job?.files?.filter((file) => file.status === "Completed").length ?? 0) < 2}
          />
        )}
        {hasResults && canCompare && (
          <SegmentedControl
            value={batchView}
            onChange={(value) => {
              if (value === "compare" || value === "review") onBatchViewChange(value);
            }}
            data={[
              { value: "compare", label: t("jobResultsPage.batchCompare.compareFiles") },
              { value: "review", label: t("jobResultsPage.batchCompare.reviewRows") },
            ]}
          />
        )}
        {showBulkAccept && (
          <BulkAcceptPopover
            opened={bulkOpen}
            onOpenedChange={onBulkOpenChange}
            disabled={!hasResults || rowCount === 0}
            running={bulkRunning}
            threshold={bulkThreshold}
            onThresholdChange={onBulkThresholdChange}
            eligibleCount={bulkEligibleCount}
            summary={bulkSummary}
            onCancel={onBulkCancel}
            onApply={onBulkApply}
          />
        )}
        <Button
          size="sm"
          variant="default"
          leftSection={<IconRefresh size={16} />}
          onClick={onRefresh}
          disabled={jobLoading}
          loading={jobLoading}
        >
          {t("jobResultsPage.actions.refresh")}
        </Button>
        <Menu shadow="md" position="bottom-end" withinPortal>
          <Menu.Target>
            <Button
              size="sm"
              leftSection={<IconDownload size={16} />}
              rightSection={<IconChevronDown size={14} />}
              loading={exportingExcel}
              disabled={!hasResults || exportingExcel || acceptedCount === 0}
            >
              {t("jobResultsPage.actions.export", { defaultValue: "Export" })}
              {hasResults && rowCount > 0 && ` (${acceptedCount}/${rowCount})`}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconFileSpreadsheet size={16} />}
              onClick={onExportExcel}
              // Unsaved edits no longer disable this: the export waits for
              // them and asks about any that failed (AB#840758).
              disabled={acceptedCount === 0}
            >
              {t("jobResultsPage.actions.exportExcel", {
                defaultValue: "Export as Excel",
              })}
            </Menu.Item>
            <Menu.Item
              leftSection={<IconJson size={16} />}
              onClick={onExport}
              disabled={acceptedCount === 0}
            >
              {editsCount > 0
                ? t("mappingServicePage.actions.exportWithEdits", {
                    count: editsCount,
                  })
                : t("jobResultsPage.actions.exportJson", {
                    defaultValue: "Export as JSON",
                  })}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Box>
  );
};

export default JobResultsTopbar;
